/**
 * ビューの中の、業務フローの図・ステージの図（F2）への導線。
 *
 * **案件は画面が受け取った番号をそのまま渡す。** 読む場所を画面の入力 1 つに揃える
 * （`common.ts` の docblock）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図とステージの図へのリンクを、案件を引き継いだまま描く。
 * @graph-connects isPjMode [calls] 案件モードかどうかの判定
 */
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isPjMode } from '../../lib/pj-mode.js';
import { pjLinkSearch } from '../pj-link.js';
import { FLOW_ROUTE, PJ_FLOW_ROUTE, STAGE_ROUTE } from '../route-paths.js';
import { VIEW_LINK_CLASS, pjLinkValue } from './common.js';

/**
 * `FlowLink` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図へのリンクの入力（開く業務フローの id・引き継ぐ案件番号・出す文言）
 * @graph-connects none
 */
export interface FlowLinkProps {
  /** 開く業務フローの id */
  flowId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
  /** リンクに出す文言 */
  children: ReactNode;
  /** 見た目の class。省けばビューのリンクの見た目 */
  className?: string;
}

/**
 * 業務フロー 1 本の図へのリンク。
 *
 * **案件を選んでいるときは行き先そのものが替わる。** 業務フローの図は検索条件の案件を読まず、
 * 案件つきの姿は案件をパスの親に置く別のルートが受け持つ。`?pj=` を付けて送っても案件は黙って
 * 外れ、マスターの図が開く。
 *
 * @param props 業務フローの id・案件番号・文言
 * @returns 業務フローの図へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本の図へのリンクを、案件のときは案件から見た業務フローへ向けて描く。
 * @graph-connects pjLinkValue [calls] 案件番号を検索条件の形へ
 * @graph-connects isPjMode [calls] 案件モードかどうかの判定
 */
export function FlowLink({
  flowId,
  pjNumber,
  children,
  className = VIEW_LINK_CLASS,
}: FlowLinkProps) {
  const pj = pjLinkValue(pjNumber);
  const id = String(flowId);

  if (isPjMode(pj)) {
    return (
      <Link to={PJ_FLOW_ROUTE} params={{ pjNumber: pj, flowId: id }} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <Link to={FLOW_ROUTE} params={{ flowId: id }} className={className}>
      {children}
    </Link>
  );
}

/**
 * `FlowOpenLink` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローを開くリンクの入力（開く業務フローの id・引き継ぐ案件番号）
 * @graph-connects none
 */
export interface FlowOpenLinkProps {
  /** 開く業務フローの id */
  flowId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
}

/**
 * カードに載せる「業務フローの図を開く」。文言もここが持つ。
 *
 * @param props 業務フローの id・案件番号
 * @returns 業務フローの図へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードに、案件を引き継いだまま業務フローの図を開くリンクを決まった文言で描く。
 * @graph-connects FlowLink [calls] 業務フローの図へのリンクの描画
 */
export function FlowOpenLink({ flowId, pjNumber }: FlowOpenLinkProps) {
  const { t } = useTranslation();

  return (
    <FlowLink flowId={flowId} pjNumber={pjNumber}>
      {t('flow.openFlow')}
    </FlowLink>
  );
}

/**
 * `StageLink` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図へのリンクの入力（開くステージの id・引き継ぐ案件番号・出す文言）
 * @graph-connects none
 */
export interface StageLinkProps {
  /** 開くステージの id */
  stageId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
  /** リンクに出す文言。省けば「ステージの図を開く」 */
  children?: ReactNode;
}

/**
 * ステージの図へのリンク。ステージの図は案件を検索条件（`?pj=`）で受けるので、案件はそこへ載せる。
 *
 * @param props ステージの id・案件番号・文言
 * @returns ステージの図へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図へのリンクを、案件を検索条件に引き継いで描く。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function StageLink({ stageId, pjNumber, children }: StageLinkProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={STAGE_ROUTE}
      params={{ stageId: String(stageId) }}
      {...pjLinkSearch(pjLinkValue(pjNumber))}
      className={VIEW_LINK_CLASS}
    >
      {children ?? t('flow.openStage')}
    </Link>
  );
}

/**
 * `BusinessFlowLink` の props。`flowId` は業務フローの id である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローへのリンクの入力（業務フローの id・引き継ぐ案件番号・出す文言）
 * @graph-connects none
 */
export interface BusinessFlowLinkProps {
  /** 開く業務フローの id */
  flowId: number;
  /** 重ねる案件番号。master を見ているなら undefined */
  pjNumber: number | undefined;
  /** リンクに出す文言 */
  children: ReactNode;
}

/**
 * 業務フローの図へのリンクを出す。業務マップ・業務詳細の
 * 画面はそれぞれの持ち主が作り替えるので、それまで呼び出し側を変えずに新しい行き先へ張る。
 *
 * @param props 業務フローの id・案件番号・文言
 * @returns 業務フローの図へのリンク
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図へのリンクを描く。
 * @graph-connects FlowLink [calls] 業務フローの図へのリンクの描画
 */
export function BusinessFlowLink({ flowId, pjNumber, children }: BusinessFlowLinkProps) {
  return (
    <FlowLink flowId={flowId} pjNumber={pjNumber}>
      {children}
    </FlowLink>
  );
}
